import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Legend from '../Legend.tsx';
import proj4 from 'proj4';
import { loadOrpGeoJson } from "./Orp.tsx";
import { createMarker } from './Markers.tsx';
import { fetchSeasonData } from './FetchSeasonData.tsx';
import { fetchRemoteOrpSeasonData } from "./Orp.tsx";
import { fetchCombinedProductData } from './FetchSeasonData.tsx';
import { fetchRemoteSensingData } from './FetchSeasonData.tsx';
import { fixedRanges } from './FixedRanges.tsx';

proj4.defs('EPSG:32633', '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');

// Fix for Leaflet icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface CheckedItems {
    vegetationSeason?: boolean;
    vegetationSeasonEnd?: boolean;
    seasonLength?: boolean;
}

interface MapComponentProps {
    checkedItems: CheckedItems;
    temperatureThreshold: number;
    selectedBand: string | null;
    selectedYear: number;
    selectedDays: number;
    selectedProduct: string;
    selectedVelicina: string | null;
    setAverageTemperatures: React.Dispatch<React.SetStateAction<number[]>>;
    refreshKey: number; // ⬅️ NEW
    language: 'cz' | 'en';
    selectedOption: string | null;
}


interface ParData {
    station: number;
    breakpoint_1: number;
    breakpoint_2: number;
}


const MapComponent: React.FC<MapComponentProps> = ({
    checkedItems,
    temperatureThreshold,
    selectedYear,
    selectedDays,
    selectedProduct,
    selectedVelicina,
    setAverageTemperatures,
    refreshKey,
    language,
    selectedOption
}) => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const [map, setMap] = useState<L.Map | null>(null);
    const [markers, setMarkers] = useState<L.Marker[]>([]);
    const [wmsLayer, setWmsLayer] = useState<L.TileLayer.WMS | null>(null);
    const geoJsonDataRef = useRef<any>(null);
    const parDataRef = useRef<{ [year: number]: ParData[] }>({});
    const remoteSensingDataRef = useRef<{ [year: number]: Record<string, { start: number | null, end: number | null }> }>({});
    const combinedDataRef = useRef<{ [year: number]: Record<string, { start: number | null, end: number | null }> }>({});
    const markerCacheRef = useRef<{ [plotId: string]: L.Marker }>({});
    const [orpMinMax, setOrpMinMax] = useState<{ min: number; max: number } | null>(null);


    // Load boundary data
    const loadBoundaryData = (mapInstance: L.Map) => {
        fetch('czech_republic.json')
            .then((response) => response.json())
            .then((boundary) => {
                if (boundary?.features?.length > 0) {
                    createMask(boundary, mapInstance);
                    mapInstance.fitBounds(L.geoJSON(boundary).getBounds());
                } else {
                    console.error('Boundary data is missing features or improperly structured.');
                }
            })
            .catch((error) => console.error('Error loading boundary:', error));
    };

    interface ParData {
        station: number;
        breakpoint_1: number | null;
        breakpoint_2: number | null;
    }

    interface ParDataEntry {
        breakpoint_1: number | null;
        breakpoint_2: number | null;
    }

    const fetchParData = async (
        year: number
    ): Promise<Record<string, ParDataEntry>> => {
        try {
            const [startText, endText] = await Promise.all([
                fetch(`/par_data/start_of_season/${year}.csv`).then(res =>
                    res.ok ? res.text() : ""
                ),
                fetch(`/par_data/end_of_season/${year}.csv`).then(res =>
                    res.ok ? res.text() : ""
                ),
            ]);

            if (!startText && !endText) {
                console.error(`No PAR data found for year ${year}`);
                return {};
            }

            // generic parser
            const parseCsv = (text: string): Record<string, number | null> => {
                const rows = text.trim().split("\n").slice(1);
                const result: Record<string, number | null> = {};

                for (const row of rows) {
                    const columns = row.split(",").map(col => col.replace(/^"|"$/g, "").trim());
                    const site = columns[0];
                    const doyStr = columns[1];
                    const doy = doyStr?.toUpperCase() === "NA" ? null : parseInt(doyStr, 10);
                    if (site) {
                        result[site] = !isNaN(doy ?? NaN) ? doy : null;
                    }
                }

                return result;
            };

            const startData = startText ? parseCsv(startText) : {};
            const endData = endText ? parseCsv(endText) : {};

            const allSites = new Set([...Object.keys(startData), ...Object.keys(endData)]);
            const merged: Record<string, ParDataEntry> = {};

            allSites.forEach(site => {
                merged[site] = {
                    breakpoint_1: startData[site] ?? null,
                    breakpoint_2: endData[site] ?? null,
                };
            });

            return merged;
        } catch (error) {
            console.error(`Error fetching PAR data for year ${year}:`, error);
            return {};
        }
    };

    const clearMapLayers = () => {
        if (!map) return;

        map.eachLayer(layer => {
            if (
                (layer instanceof L.GeoJSON || layer instanceof L.Marker || layer instanceof L.Polygon) &&
                !boundaryLayersRef.current.includes(layer) // Nemaz hranice
            ) {
                map.removeLayer(layer);
            }
        });

        markers.forEach(marker => map.removeLayer(marker));
        setMarkers([]);

        markerCacheRef.current = {};
        setOrpMinMax(null);
    };


    // Initial map creation
    useEffect(() => {
        if (!mapContainerRef.current || map) return;

        const mapInstance = L.map(mapContainerRef.current).setView([49.8, 16], 8);
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
        }).addTo(mapInstance);

        loadBoundaryData(mapInstance);
        setMap(mapInstance);

        return () => {
            mapInstance.remove();
        };
    }, []);


    // Initial load
    useEffect(() => {
        if (!map) return;

        // Najskôr vyčisti mapu
        clearMapLayers();

        if (["meteorologicalData", "dpz", "combinedProduct"].includes(selectedProduct)) {
            console.log("📌 Loading ORP layers for product:", selectedProduct);

            loadOrpGeoJson(
                map,
                selectedYear,
                selectedDays,
                setAverageTemperatures,
                checkedItems,
                temperatureThreshold,
                setOrpMinMax,
                selectedProduct,
                language
            );
        } else {
            console.log("❌ Skipping ORP layer for product:", selectedProduct);
        }
    }, [map, selectedYear, selectedDays, checkedItems, temperatureThreshold, selectedProduct]);


    // Update ORP data when parameters change
    useEffect(() => {
        if (!map) return;

        if (["meteorologicalData", "dpz", "combinedProduct"].includes(selectedProduct)) {
            console.log("📌 Loading ORP layers for product:", selectedProduct);

            loadOrpGeoJson(
                map,
                selectedYear,
                selectedDays,
                setAverageTemperatures,
                checkedItems,
                temperatureThreshold,
                setOrpMinMax,
                selectedProduct,
                language
            );
        } else {
            console.log("❌ Skipping ORP layer for product:", selectedProduct);

            map.eachLayer(layer => {
                if (layer instanceof L.GeoJSON) {
                    map.removeLayer(layer);
                }
            });

            setOrpMinMax(null);
        }
    }, [map, selectedYear, selectedDays, checkedItems, temperatureThreshold, selectedProduct]);

    useEffect(() => {
        if (!map || !geoJsonDataRef.current) return;

        clearMapLayers();

        // 🟢 Znovu načítaj ORP vrstvu, ak je aktívna
        if (["meteorologicalData", "dpz", "combinedProduct"].includes(selectedProduct)) {
            loadOrpGeoJson(
                map,
                selectedYear,
                selectedDays,
                setAverageTemperatures,
                checkedItems,
                temperatureThreshold,
                setOrpMinMax,
                selectedProduct,
                language
            );
        }

        // 🟢 Znovu načítaj markery
        fetchMarkers(
            map,
            checkedItems,
            temperatureThreshold,
            selectedYear,
            selectedDays,
            selectedProduct,
            selectedVelicina,
            language
        );
    }, [language]);




    useEffect(() => {
        if (!map) return;

        fetch('/stations.csv')
            .then((res) => res.text())
            .then((csvText) => {
                const lines = csvText.trim().split('\n');
                const headers = lines[0].split(';');

                const features = lines.slice(1).map((line) => {
                    const cols = line.split(';');
                    const obj: any = {};
                    headers.forEach((h, i) => {
                        obj[h] = cols[i];
                    });

                    return {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [parseFloat(obj.longitude), parseFloat(obj.latitude)]
                        },
                        properties: {
                            plot_ID: obj.plot_ID,
                            plot_name: obj.plot_name,
                            NAZ_ORP: obj.plot_name // ak nemáš iné ORP, použi plot_name ako náhradu
                        }
                    };
                });

                const geojson = {
                    type: 'FeatureCollection',
                    features
                };

                geoJsonDataRef.current = geojson;
                markerCacheRef.current = {};
                setMarkers([]);

                fetchMarkers(
                    map,
                    checkedItems,
                    temperatureThreshold,
                    selectedYear,
                    selectedDays,
                    selectedProduct,
                    selectedVelicina,
                    language
                );
            })
            .catch((err) => console.error("Failed to load stations CSV", err));
    }, [map, selectedYear, selectedProduct, selectedVelicina, selectedDays, checkedItems, temperatureThreshold]);



    useEffect(() => {
        if (!map || !geoJsonDataRef.current) return;

        // Najskôr vyčisti mapu
        clearMapLayers();

        fetchMarkers(
            map,
            checkedItems,
            temperatureThreshold,
            selectedYear,
            selectedDays,
            selectedProduct,
            selectedVelicina,
            language
        );
    }, [map, selectedYear, selectedDays, selectedProduct, selectedVelicina, temperatureThreshold, checkedItems]);




    // Fetch markers whenever conditions change
    useEffect(() => {
        if (!map || !geoJsonDataRef.current) return;

        clearMapLayers();

        fetchMarkers(
            map,
            checkedItems,
            temperatureThreshold,
            selectedYear,
            selectedDays,
            selectedProduct,
            selectedVelicina,
            language
        );
    }, [refreshKey]);



    const fetchAverageTemperatures = async (year: number, plotId: string) => {
        try {
            const response = await fetch(`/average_temperatures/${year}.csv`);
            if (!response.ok) {
                console.error(`Failed to fetch CSV for year ${year}:`, response.statusText);
                return { temperatures: [], temperatureDays: [] };
            }

            const text = await response.text();
            console.log(`Raw CSV for year ${year}:`, text);

            const rows = text.trim().split('\n').map(row => row.trim());
            if (rows.length < 2) {
                console.error(`CSV for year ${year} is empty or improperly formatted.`);
                return { temperatures: [], temperatureDays: [] };
            }

            // Parse headers
            const headers = rows.shift()?.split(';') || [];
            console.log(`Headers for year ${year}:`, headers);

            if (headers.length < 2) {
                console.error(`CSV for year ${year} is missing valid headers.`);
                return { temperatures: [], temperatureDays: [] };
            }

            // Find index of the clicked plot
            const plotIndex = headers.indexOf(plotId);
            if (plotIndex === -1) {
                console.error(`Plot ${plotId} not found in CSV headers for year ${year}. Available headers:`, headers);
                return { temperatures: [], temperatureDays: [] };
            }

            // Convert MM-DD to DOY and extract temperatures
            const temperatures: number[] = [];
            const temperatureDays: number[] = [];

            rows.forEach(row => {
                const values = row.split(';');
                const dateString = values[0]; // Format: MM-DD

                // Convert dateString (04-03) to DOY
                const doy = getDOY(dateString, year);
                if (!isNaN(doy)) {
                    temperatureDays.push(doy);
                    temperatures.push(parseFloat(values[plotIndex]) || NaN);
                }
            });

            console.log(`Extracted Temperatures:`, temperatures);
            console.log(`Temperature Days (DOY):`, temperatureDays);
            return { temperatures, temperatureDays };
        } catch (error) {
            console.error(`Error processing CSV for year ${year}:`, error);
            return { temperatures: [], temperatureDays: [] };
        }
    };

    const getDOY = (dateString: string, year: number): number => {
        // Ensure the date string is in MM-DD format
        const [month, day] = dateString.split('-').map(Number);
        if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
            console.error(`Invalid date string: ${dateString}`);
            return NaN;
        }

        const fullDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const date = new Date(fullDateString);

        if (isNaN(date.getTime())) {
            console.error(`Invalid date string: ${dateString}`);
            return NaN;
        }

        const startOfYear = new Date(year, 0, 0); // Start of the year (December 31 of the previous year)
        const diff = date.getTime() - startOfYear.getTime();

        // Convert milliseconds to days
        return Math.floor(diff / (1000 * 60 * 60 * 24));
    };

    const fetchMarkers = async (
        map: L.Map,
        checkedItems: CheckedItems,
        threshold: number,
        selectedYear: number,
        selectedDays: number,
        selectedProduct: string,
        selectedVelicina: string | null,
        language: 'cz' | 'en'
    ) => {
        if (!geoJsonDataRef.current) return;

        // Load PAR sensor data
        if (selectedProduct === "parSensors" && !parDataRef.current[selectedYear]) {
            const parData = await fetchParData(selectedYear);
            parDataRef.current[selectedYear] = parData;
        }

        // Load DPZ remote sensing data
        if (selectedProduct === "dpz" && !remoteSensingDataRef.current[selectedYear]) {
            const remoteData = await fetchRemoteSensingData(selectedYear);
            remoteSensingDataRef.current[selectedYear] = remoteData;
        }

        // 🔄 Load combined data
        if (selectedProduct === "combinedProduct" && !combinedDataRef.current?.[selectedYear]) {
            const combined = await fetchCombinedProductData(selectedYear);
            combinedDataRef.current[selectedYear] = combined;
        }

        const parData = parDataRef.current[selectedYear] || {};
        const remoteData = remoteSensingDataRef.current[selectedYear] || {};
        const combinedData = combinedDataRef.current?.[selectedYear] || {};

        const newMarkers: L.Marker[] = [];

        markers.forEach(m => map.removeLayer(m));
        setMarkers([]);

        for (const feature of geoJsonDataRef.current.features) {
            const { coordinates } = feature.geometry;
            const { plot_ID, plot_name: place } = feature.properties;
            const orpName = feature.properties.NAZ_ORP;

            let seasonStart: { doy: number | null, date: string | null } | null = null;
            let seasonEnd: { doy: number | null, date: string | null } | null = null;
            let seasonLength: number | null = null;

            if (selectedProduct === "meteorologicalData") {
                seasonStart = await fetchSeasonData(plot_ID, `${threshold}C`, selectedYear, selectedDays, 'start', 'plot');
                seasonEnd = await fetchSeasonData(plot_ID, `${threshold}C`, selectedYear, selectedDays, 'end', 'plot');
                if (seasonStart?.doy !== null && seasonEnd?.doy !== null) {
                    seasonLength = seasonEnd.doy - seasonStart.doy;
                }
            }

            if (selectedProduct === "dpz") {
                seasonStart = await fetchRemoteOrpSeasonData(orpName, selectedYear, "start", "dpz");
                seasonEnd = await fetchRemoteOrpSeasonData(orpName, selectedYear, "end", "dpz");
                if (seasonStart?.doy !== null && seasonEnd?.doy !== null) {
                    seasonLength = seasonEnd.doy - seasonStart.doy;
                }
            }

            if (selectedProduct === "combinedProduct") {
                seasonStart = await fetchRemoteOrpSeasonData(orpName, selectedYear, "start", "combined");
                seasonEnd = await fetchRemoteOrpSeasonData(orpName, selectedYear, "end", "combined");
                if (seasonStart?.doy !== null && seasonEnd?.doy !== null) {
                    seasonLength = seasonEnd.doy - seasonStart.doy;
                }
            }


            // Vypočítaj dĺžku sezóny ak sú k dispozícii DOY
            if (
                (selectedProduct === "meteorologicalData" || selectedProduct === "dpz" || selectedProduct === "combinedProduct") &&
                seasonStart?.doy !== null &&
                seasonEnd?.doy !== null
            ) {
                seasonLength = seasonEnd!.doy - seasonStart!.doy;
            }


            const stationParData = selectedProduct === "parSensors" ? parData[place.trim()] : undefined;
            const remoteRecord = selectedProduct === "dpz" ? remoteData[place?.trim()] : undefined;
            const remoteSeasonStart = remoteRecord?.start ?? null;
            const remoteSeasonEnd = remoteRecord?.end ?? null;
            const remoteSeasonLength = (remoteSeasonStart !== null && remoteSeasonEnd !== null)
                ? remoteSeasonEnd - remoteSeasonStart
                : null;

            const combinedRecord = selectedProduct === "combinedProduct" ? combinedData[place?.trim()] : undefined;
            const combinedSeasonStart = combinedRecord?.start ?? null;
            const combinedSeasonEnd = combinedRecord?.end ?? null;
            const combinedSeasonLength = (combinedSeasonStart !== null && combinedSeasonEnd !== null)
                ? combinedSeasonEnd - combinedSeasonStart
                : null;

            const info = {
                plot_ID,
                place,
                seasonStart,
                seasonEnd,
                seasonLength,
                stationParData,
                remoteSeasonStart,
                remoteSeasonEnd,
                remoteSeasonLength,
                combinedSeasonStart,
                combinedSeasonEnd,
                combinedSeasonLength
            };

            let showMarker = false;

            if (selectedProduct === "meteorologicalData") {
                showMarker =
                    (!!checkedItems.vegetationSeason && info.seasonStart?.doy !== null) ||
                    (!!checkedItems.vegetationSeasonEnd && info.seasonEnd?.doy !== null) ||
                    (!!checkedItems.seasonLength && info.seasonLength !== null);
            }

            if (selectedProduct === "parSensors" && info.stationParData) {
                showMarker =
                    (!!checkedItems.vegetationSeason && info.stationParData.breakpoint_1 !== null) ||
                    (!!checkedItems.vegetationSeasonEnd && info.stationParData.breakpoint_2 !== null) ||
                    (!!checkedItems.seasonLength &&
                        info.stationParData.breakpoint_1 !== null &&
                        info.stationParData.breakpoint_2 !== null);
            }

            if (selectedProduct === "dpz") {
                showMarker =
                    (!!checkedItems.vegetationSeason && info.remoteSeasonStart !== null) ||
                    (!!checkedItems.vegetationSeasonEnd && info.remoteSeasonEnd !== null) ||
                    (!!checkedItems.seasonLength &&
                        info.remoteSeasonStart !== null &&
                        info.remoteSeasonEnd !== null);
            }

            if (selectedProduct === "combinedProduct") {
                showMarker =
                    (!!checkedItems.vegetationSeason && info.combinedSeasonStart !== null) ||
                    (!!checkedItems.vegetationSeasonEnd && info.combinedSeasonEnd !== null) ||
                    (!!checkedItems.seasonLength &&
                        info.combinedSeasonStart !== null &&
                        info.combinedSeasonEnd !== null);
            }

            const marker = createMarker(
                [coordinates[1], coordinates[0]],
                info,
                selectedProduct,
                checkedItems,
                selectedYear,
                fetchAverageTemperatures,
                setAverageTemperatures,
                language
            );

            markerCacheRef.current[plot_ID] = marker;

            if (!map.hasLayer(marker)) marker.addTo(map);
            if (!showMarker) marker.remove();

            newMarkers.push(marker);
        }

        setMarkers(newMarkers);
    };

    const boundaryLayersRef = useRef<L.Layer[]>([]);

    // Create mask for the boundary
    const createMask = (boundary: any, mapInstance: L.Map) => {
        const outerBoundary = [[[-90, -180], [-90, 180], [90, 180], [90, -180]]];
        const czechPolygon = boundary.features[0].geometry.coordinates[0].map((coord: [number, number]) => [
            coord[1],
            coord[0],
        ]);

        const mask = L.polygon([outerBoundary[0], czechPolygon], { fillColor: '#888', fillOpacity: 0.5, interactive: false }).addTo(mapInstance);
        const border = L.geoJSON(boundary, { style: { color: 'black', weight: 2, fillOpacity: 0 } }).addTo(mapInstance);

        // Ulož si vrstvy, aby si ich nevymazal
        boundaryLayersRef.current = [mask, border];
    };

    const isStart = !!checkedItems.vegetationSeason;
    const useFixedRange = isStart && (selectedProduct === 'dpz' || selectedProduct === 'combinedProduct');


    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Map Container */}
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }}></div>

            {/* Legend */}
            {["meteorologicalData", "dpz", "combinedProduct"].includes(selectedProduct) &&
                (checkedItems.vegetationSeason || checkedItems.vegetationSeasonEnd || checkedItems.seasonLength) &&
                orpMinMax &&
                !selectedOption && ( // ← presunuté sem
                    <div
                        style={{
                            position: 'absolute',
                            bottom: '20px',
                            left: '20px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '10px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            zIndex: 1000,
                            width: '220px'
                        }}
                    >
                        <Legend
                            min={orpMinMax.min}
                            max={orpMinMax.max}
                            title={
                                checkedItems.vegetationSeason
                                    ? (language === 'cz' ? "Začátek sezony (DOY)" : "Start of season (DOY)")
                                    : checkedItems.vegetationSeasonEnd
                                        ? (language === 'cz' ? "Konec sezony (DOY)" : "End of season (DOY)")
                                        : checkedItems.seasonLength
                                            ? (language === 'cz' ? "Délka sezony (dní)" : "Season length (days)")
                                            : ""
                            }
                            unit={checkedItems.seasonLength ? (language === 'cz' ? "dní" : "days") : "DOY"}
                            reverse={checkedItems.vegetationSeason}
                            fixedRange={
                                selectedProduct === "combinedProduct"
                                    ? (
                                        checkedItems.vegetationSeason ? fixedRanges.combinedProduct.SOS :
                                            checkedItems.seasonLength ? fixedRanges.combinedProduct.LOS :
                                                checkedItems.vegetationSeasonEnd ? fixedRanges.combinedProduct.EOS :
                                                    undefined
                                    )
                                    : selectedProduct === "dpz"
                                        ? (
                                            checkedItems.vegetationSeason ? fixedRanges.dpz.SOS :
                                                checkedItems.seasonLength ? fixedRanges.dpz.LOS :
                                                    checkedItems.vegetationSeasonEnd ? fixedRanges.dpz.EOS :
                                                        undefined
                                        )
                                        : undefined
                            }
                        />

                    </div>
                )}
        </div>
    );
};

export default MapComponent;
